/**
 * notifications.ts — email notifications (sent through Resend; the key is entered in Admin → API keys).
 *
 * OFF by default per site. A manager switches the site on, then picks which events send mail. The person who
 * caused an event is never emailed about it. Mail is best effort: a failed send never blocks or undoes the
 * action that caused it, and every attempt is recorded in the email log so a manager can see why one did not arrive.
 */
import { z } from "zod";

export const NOTIFICATION_EVENTS = ["review_requested", "rejected", "published", "publish_problem"] as const;
export const notificationEventSchema = z.enum(NOTIFICATION_EVENTS);
export type NotificationEvent = z.infer<typeof notificationEventSchema>;

export const NOTIFICATION_EVENT_INFO: Record<NotificationEvent, { label: string; who: string }> = {
  review_requested: { label: "Someone submits an item for review", who: "Everyone who can publish" },
  rejected: { label: "An item is sent back for changes", who: "The person who created it" },
  published: { label: "An item goes live", who: "The person who created it" },
  publish_problem: { label: "A scheduled item is held or fails to publish", who: "Everyone who can publish" },
};

export const notificationSettingsSchema = z.object({
  siteId: z.string(),
  /** Master switch. Nothing is sent while this is off. */
  enabled: z.boolean(),
  events: z.record(notificationEventSchema, z.boolean()),
});
export type NotificationSettings = z.infer<typeof notificationSettingsSchema>;

export const updateNotificationSettingsSchema = z.object({
  enabled: z.boolean().optional(),
  events: z.record(notificationEventSchema, z.boolean()).optional(),
});
export type UpdateNotificationSettingsInput = z.infer<typeof updateNotificationSettingsSchema>;

export const emailLogEntrySchema = z.object({
  id: z.string(),
  event: z.string(),
  to: z.string(),
  subject: z.string(),
  status: z.enum(["sent", "failed", "skipped"]),
  /** Plain-language reason for failed / skipped. */
  detail: z.string().nullable(),
  createdAt: z.string(),
});
export type EmailLogEntry = z.infer<typeof emailLogEntrySchema>;

export const notificationsResponseSchema = z.object({
  settings: notificationSettingsSchema,
  /** The Resend key and sender are both saved, so mail can be sent at all. */
  emailReady: z.boolean(),
  log: z.array(emailLogEntrySchema),
});
export type NotificationsResponse = z.infer<typeof notificationsResponseSchema>;

export const testEmailResultSchema = z.object({ ok: z.boolean(), message: z.string() });

export function defaultNotificationEvents(): Record<NotificationEvent, boolean> {
  return { review_requested: true, rejected: true, published: true, publish_problem: true };
}

const oneLine = (s: string, max: number) => s.replace(/[\r\n\u2028\u2029]+/g, " ").replace(/\s+/g, " ").trim().slice(0, max);
const escapeHtml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export interface NotificationInput {
  event: NotificationEvent;
  siteName: string;
  itemTitle: string;
  /** Link to the item in the CMS. */
  itemUrl: string;
  /** Who did it, when a person did (null for the automatic publisher). */
  actorName: string | null;
  /** Reason or extra line (rejection note, why a publish was held, where it went live). */
  detail?: string | null;
}

/** The subject and body of one notification. Pure, so it is tested without sending anything. */
export function buildNotificationEmail(input: NotificationInput): { subject: string; text: string; html: string } {
  const title = oneLine(input.itemTitle || "Untitled", 120);
  const site = oneLine(input.siteName, 60);
  const actor = input.actorName ? oneLine(input.actorName, 80) : "The scheduler";
  const detail = input.detail ? oneLine(input.detail, 500) : null;

  let subject: string;
  let line: string;
  switch (input.event) {
    case "review_requested":
      subject = `Review requested: ${title}`;
      line = `${actor} submitted “${title}” for review on ${site}.`;
      break;
    case "rejected":
      subject = `Changes requested: ${title}`;
      line = `${actor} sent “${title}” back for changes on ${site}.`;
      break;
    case "published":
      subject = `Published: ${title}`;
      line = `“${title}” is now live on ${site}.`;
      break;
    case "publish_problem":
      subject = `Needs attention: ${title}`;
      line = `The scheduled publish of “${title}” on ${site} did not go out.`;
      break;
  }
  const text = [line, detail, "", `Open it: ${input.itemUrl}`, "", "You get this because email notifications are on for this site. A manager can change that in the CMS under Notifications."]
    .filter((l) => l !== null)
    .join("\n");
  const html =
    `<div style="font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;font-size:15px;line-height:1.5;color:#1a1a1a">` +
    `<p style="margin:0 0 12px">${escapeHtml(line)}</p>` +
    (detail ? `<p style="margin:0 0 12px;color:#555">${escapeHtml(detail)}</p>` : "") +
    `<p style="margin:0 0 20px"><a href="${escapeHtml(input.itemUrl)}">Open it in the CMS</a></p>` +
    `<p style="margin:0;font-size:12px;color:#888">You get this because email notifications are on for this site. A manager can change that under Notifications.</p></div>`;
  return { subject, text, html };
}
